/* eslint-disable react/no-array-index-key */
import React from 'react';

import { Heading, Paragraph, List, ListItem, Link } from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { TaggedAccordion } from 'client/components/taggedAccordion/TaggedAccordion';
import testIds from 'client/testIds';

import { LimitedCompanyCriteriaContent } from '../LimitedCompanyCriteria.config';
import BackToTopLink from './BackToTopLink';

function PropertySection() {
  const { property, backToTopLabel, jumpToASection } =
    useContent<LimitedCompanyCriteriaContent>();

  const {
    id,
    title,
    location,
    builderCashIncentives,
    buyToLetPropertyAddress,
    cladding,
    condition,
    construction,
    distressedSaleAndLeaseback,
    energyPerformanceCertificate,
    exposureLimits,
    freeValuations,
    housesInMultipleOccupations,
    japaneseKnotweed,
    newBuild,
    revisedPropertyDetails,
    scottishValuations,
    subSalesAndBackToBackTransactions,
    tenure,
    unacceptableConstructionTypes,
    useOfProperty,
    valuation,
  } = property;

  return (
    <section
      id={id}
      data-testid={testIds.limitedCompanyCriteria.propertySection}
    >
      <Heading
        size="s5"
        marginBottom="05"
        marginTop="05"
        as="h2"
        dataTestId={testIds.limitedCompanyCriteria.propertyHeading}
      >
        {title}
      </Heading>

      <TaggedAccordion
        id={location.id}
        label={
          <Heading size="s3" as="h3">
            {location.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.location}
      >
        <Paragraph>{location.paragraph}</Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        id={builderCashIncentives.id}
        label={
          <Heading size="s3" as="h3">
            {builderCashIncentives.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.builderCashIncentives}
      >
        {builderCashIncentives.paragraphs.map((item, idx) => (
          <Paragraph key={`builder-cash-incentives-${idx}`}>{item}</Paragraph>
        ))}
        <Heading as="h4" size="s2" marginTop="04">
          {builderCashIncentives.additionalInfo.title}
        </Heading>
        {builderCashIncentives.additionalInfo.links.map((link, idx) => (
          <Paragraph
            marginBottom="02"
            key={`builder-cash-incentives-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={buyToLetPropertyAddress.id}
        label={
          <Heading size="s3" as="h3">
            {buyToLetPropertyAddress.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.buyToLetPropertyAddress}
      >
        <Paragraph>{buyToLetPropertyAddress.paragraph}</Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        id={cladding.id}
        label={
          <Heading size="s3" as="h3">
            {cladding.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.cladding}
      >
        <Heading as="h4" size="s2" marginTop="04">
          {cladding.propertiesInEnglandAndWales.title}
        </Heading>
        {cladding.propertiesInEnglandAndWales.paragraphs.map((item, idx) => (
          <Paragraph key={`cladding-ew-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {cladding.propertiesInScotland.title}
        </Heading>
        {cladding.propertiesInScotland.paragraphs.map((item, idx) => (
          <Paragraph key={`cladding-sc-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {cladding.newBuildHighRiseBlocks.title}
        </Heading>
        {cladding.newBuildHighRiseBlocks.paragraphs.map((item, idx) => (
          <Paragraph key={`cladding-nbhrb-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={condition.id}
        label={
          <Heading size="s3" as="h3">
            {condition.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.condition}
      >
        {condition.paragraphs.map((item, idx) => (
          <Paragraph key={`condition-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={construction.id}
        label={
          <Heading size="s3" as="h3">
            {construction.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.construction}
      >
        {construction.paragraphs.map((item, idx) => (
          <Paragraph key={`construction-${idx}`}>{item}</Paragraph>
        ))}
        <List marginBottom="05">
          {construction.listItems.map((item, idx) => (
            <ListItem key={`construction-list-${idx}`}>{item}</ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        id={distressedSaleAndLeaseback.id}
        label={
          <Heading size="s3" as="h3">
            {distressedSaleAndLeaseback.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.distressedSaleAndLeaseback}
      >
        <Paragraph>{distressedSaleAndLeaseback.paragraph}</Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        id={energyPerformanceCertificate.id}
        label={
          <Heading size="s3" as="h3">
            {energyPerformanceCertificate.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.energyPerformanceCertificate}
      >
        <Paragraph>{energyPerformanceCertificate.paragraph}</Paragraph>
        <List>
          {energyPerformanceCertificate.listItems.map((item, idx) => (
            <ListItem key={`epc-list-${idx}`}>{item}</ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        id={exposureLimits.id}
        label={
          <Heading size="s3" as="h3">
            {exposureLimits.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.exposureLimits}
      >
        {exposureLimits.paragraphs.map((item, idx) => (
          <Paragraph key={`exposure-limits-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={freeValuations.id}
        label={
          <Heading size="s3" as="h3">
            {freeValuations.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.freeValuations}
      >
        {freeValuations.paragraphs.map((item, idx) => (
          <Paragraph key={`free-valuations-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={housesInMultipleOccupations.id}
        label={
          <Heading size="s3" as="h3">
            {housesInMultipleOccupations.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.housesInMultipleOccupations}
      >
        <Paragraph>{housesInMultipleOccupations.paragraph}</Paragraph>
        <List marginBottom="05">
          {housesInMultipleOccupations.listItems.map((item, idx) => (
            <ListItem key={`hmo-list-${idx}`}>{item}</ListItem>
          ))}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {housesInMultipleOccupations.additionalInfo.title}
        </Heading>
        {housesInMultipleOccupations.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`hmo-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={japaneseKnotweed.id}
        label={
          <Heading size="s3" as="h3">
            {japaneseKnotweed.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.japaneseKnotweed}
      >
        {japaneseKnotweed.paragraphs.map((item, idx) => (
          <Paragraph key={`japanese-knotweed-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={newBuild.id}
        label={
          <Heading size="s3" as="h3">
            {newBuild.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.newBuild}
      >
        <Paragraph>{newBuild.paragraph1}</Paragraph>
        <List marginBottom="05">
          {newBuild.listItems1.map((item, idx) => (
            <ListItem key={`new-build-list1-${idx}`}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{newBuild.paragraph2}</Paragraph>
        <List marginBottom="05">
          {newBuild.listItems2.map((item, idx) => (
            <ListItem key={`new-build-list2-${idx}`}>{item}</ListItem>
          ))}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {newBuild.forPropertyToQualify.title}
        </Heading>
        <List>
          {newBuild.forPropertyToQualify.listItems.map((item, idx) => (
            <ListItem key={`new-build-qualify-${idx}`}>{item}</ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        id={revisedPropertyDetails.id}
        label={
          <Heading size="s3" as="h3">
            {revisedPropertyDetails.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.revisedPropertyDetails}
      >
        {revisedPropertyDetails.paragraphs.map((item, idx) => (
          <Paragraph key={`revised-property-details-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={scottishValuations.id}
        label={
          <Heading size="s3" as="h3">
            {scottishValuations.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.scottishValuations}
      >
        {scottishValuations.paragraphs.map((item, idx) => (
          <Paragraph key={`scottish-valuations-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        id={subSalesAndBackToBackTransactions.id}
        label={
          <Heading size="s3" as="h3">
            {subSalesAndBackToBackTransactions.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.subSalesAndBackToBackTransactions
        }
      >
        {subSalesAndBackToBackTransactions.paragraphs.map((item, idx) => (
          <Paragraph key={`sub-sales-b2b-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {
            subSalesAndBackToBackTransactions.wellAcceptBeingSoldWhereSalesAre
              .title
          }
        </Heading>
        <List marginBottom="05">
          {subSalesAndBackToBackTransactions.wellAcceptBeingSoldWhereSalesAre.listItems.map(
            (item, idx) => (
              <ListItem key={`sub-sales-b2b-list-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
        {subSalesAndBackToBackTransactions.wellAcceptBeingSoldWhereSalesAre.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`sub-sales-b2b-para-${idx}`}>{item}</Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        id={tenure.id}
        label={
          <Heading size="s3" as="h3">
            {tenure.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.tenure}
      >
        {tenure.paragraph.map((item, idx) => (
          <Paragraph key={`tenure-para-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {tenure.tenureLeaseholdDoesNotOwnFreehold.title}
        </Heading>
        <List marginBottom="05">
          {tenure.tenureLeaseholdDoesNotOwnFreehold.listItems.map(
            (item, idx) => (
              <ListItem key={`tenure-lh-no-freehold-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {tenure.tenureLeaseholdWillOwnWholeFreehold.title}
        </Heading>
        <List marginBottom="05">
          {tenure.tenureLeaseholdWillOwnWholeFreehold.listItems.map(
            (item, idx) => (
              <ListItem key={`tenure-lh-whole-freehold-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {tenure.tenureLeaseholdWillOwnShareFreehold.title}
        </Heading>
        <List marginBottom="05">
          {tenure.tenureLeaseholdWillOwnShareFreehold.listItems.map(
            (item, idx) => (
              <ListItem key={`tenure-lh-share-freehold-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {tenure.tenureFreehold.title}
        </Heading>
        <List marginBottom="05">
          {tenure.tenureFreehold.listItems.map((item, idx) => (
            <ListItem key={`tenure-freehold-${idx}`}>{item}</ListItem>
          ))}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {tenure.moreThanFourUnits.title}
        </Heading>
        <List>
          {tenure.moreThanFourUnits.listItems.map((item, idx) => (
            <ListItem key={`tenure-more-than-four-${idx}`}>{item}</ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        id={unacceptableConstructionTypes.id}
        label={
          <Heading size="s3" as="h3">
            {unacceptableConstructionTypes.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.unacceptableConstructionTypes
        }
      >
        <List>
          {unacceptableConstructionTypes.listItems.map((item, idx) => (
            <ListItem key={`unacceptable-construction-${idx}`}>{item}</ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        id={useOfProperty.id}
        label={
          <Heading size="s3" as="h3">
            {useOfProperty.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.useOfProperty}
      >
        <Heading as="h4" size="s2" marginTop="04">
          {useOfProperty.notAcceptableAsSecurity.title}
        </Heading>
        <List>
          {useOfProperty.notAcceptableAsSecurity.listItems.map((item, idx) => (
            <ListItem key={`use-of-property-not-acceptable-${idx}`}>
              {item}
            </ListItem>
          ))}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        id={valuation.id}
        label={
          <Heading size="s3" as="h3">
            {valuation.title}
          </Heading>
        }
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.valuation}
      >
        {valuation.paragraphs.map((item, idx) => (
          <Paragraph key={`valuation-para-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {valuation.additionalInfo.title}
        </Heading>
        {valuation.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`valuation-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <BackToTopLink
        dataTestId={`${testIds.limitedCompanyCriteria.propertySection}-back-to-top`}
        jumpToASectionId={jumpToASection.id}
      >
        {backToTopLabel}
      </BackToTopLink>
    </section>
  );
}

export default PropertySection;
